/**
 * Image Preloader Utility
 * 
 * Utilities for preloading images and prefetching pages
 * to ensure smooth user experience without visible loading
 */

/**
 * Preload a single image
 * Returns a promise that resolves when the image is loaded
 */
export function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
    img.src = url;
  });
}

/**
 * Preload multiple images
 * Returns a promise that resolves when all images are loaded
 */
export async function preloadImages(urls: string[]): Promise<void> {
  try {
    await Promise.all(urls.map(url => preloadImage(url)));
  } catch (error) {
    console.warn('Some images failed to preload:', error);
  }
}

/**
 * Preload images during idle time (non-blocking)
 * Uses requestIdleCallback for better performance
 */
export function preloadImagesIdle(urls: string[]): void {
  if (typeof window === 'undefined') return;
  
  const requestIdleCallback = window.requestIdleCallback || 
    ((cb: IdleRequestCallback) => setTimeout(cb, 1));
  
  requestIdleCallback(() => {
    preloadImages(urls).catch(err => 
      console.warn('Idle preload failed:', err)
    );
  });
}

/**
 * Prefetch a page (including its HTML, CSS, JS)
 * Uses link prefetch for efficient loading
 */
export function prefetchPage(url: string): void {
  if (typeof document === 'undefined') return;
  
  // Check if already prefetched
  const existing = document.querySelector(`link[rel="prefetch"][href="${url}"]`);
  if (existing) return;
  
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = url;
  link.as = 'document';
  document.head.appendChild(link);
}

/**
 * Preconnect to an origin for faster subsequent requests
 */
export function preconnectOrigin(origin: string): void {
  if (typeof document === 'undefined') return;
  
  const existing = document.querySelector(`link[rel="preconnect"][href="${origin}"]`);
  if (existing) return;
  
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

/**
 * Menu images are now handled by Next.js Image component with priority prop
 * No manual preloading needed - Next.js automatically optimizes to 48x48px versions
 */

/**
 * Preload images for a specific page on hover
 * Used for predictive preloading when user hovers over links
 */
export function preloadPageImages(pagePath: string): void {
  if (typeof window === 'undefined') return;
  
  const pageImageMap: Record<string, string[]> = {
    '/all-you-can-eat': [
      '/images/ayce-case-studies/ayce-qr-ordering-shabu-shabu-menu-1.webp',
      '/images/ayce-case-studies/ayce-tablet-ordering-shabu-shabu-menu-1.webp',
      '/images/ayce-case-studies/ayce-qr-ordering-okinii-menu-1.webp',
      '/images/ayce-case-studies/ayce-tablet-ordering-okinii-menu-1.webp',
    ],
  };
  
  const images = pageImageMap[pagePath];
  if (images && images.length > 0) {
    preloadImages(images).catch(err => 
      console.warn(`Failed to preload images for ${pagePath}:`, err)
    );
  }
}

